import { Outlet, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { CalendarClock, CalendarDays, ChartNoAxesColumn, FolderOpen, History, ListPlus, Users } from "lucide-react";
import { lazy, Suspense, useState, type ReactNode } from "react";
import { useEu } from "../sessao/eu";
import { supabase } from "../sessao/supabase";
import { useAtalhoBusca } from "./kit/atalhoBusca";
import type { ItemBusca } from "./kit/BuscaRapida";
import { MolduraBase, type ItemMenu } from "./kit/MolduraBase";
import { PainelContexto } from "./kit/painel";

// A busca (cmdk) só baixa na primeira vez que alguém abre.
const BuscaRapida = lazy(() => import("./kit/BuscaRapida").then((m) => ({ default: m.BuscaRapida })));

type Item = ItemMenu & { gestor?: boolean };

// Esconder o item é só conforto: quem decide o acesso é a API (constituição §3.2, §3.7).
export const ITENS: Item[] = [
  { para: "/", rotulo: "Tarefas", icone: CalendarDays, exato: true },
  { para: "/processos", rotulo: "Processos", icone: FolderOpen },
  { para: "/agenda", rotulo: "Agenda", icone: CalendarClock },
  { para: "/lancamentos", rotulo: "Lançamentos", icone: ListPlus },
  { para: "/carga", rotulo: "Carga da equipe", icone: Users, gestor: true },
  { para: "/painel", rotulo: "Painel", icone: ChartNoAxesColumn, gestor: true },
  { para: "/alteracoes", rotulo: "Alterações", icone: History, gestor: true },
];

export function Moldura() {
  const { data: eu } = useEu();
  const navegar = useNavigate();
  const qc = useQueryClient();
  const [busca, setBusca] = useState(false);
  const [painel, setPainel] = useState<ReactNode>(null);
  const [buscaUsada, setBuscaUsada] = useState(false);
  const abrirBusca = (v: boolean) => { if (v) setBuscaUsada(true); setBusca(v); };
  useAtalhoBusca(busca, abrirBusca);

  async function sair() {
    await supabase.auth.signOut();
    qc.clear();
    await navegar({ to: "/entrar" });
  }

  const itens = ITENS.filter((i) => !i.gestor || eu?.gestor);
  const atalhos: ItemBusca[] = [
    ...itens.map((i) => ({ id: i.para, grupo: "Ir para", rotulo: i.rotulo, icone: <i.icone size={16} aria-hidden />, aoEscolher: () => navegar({ to: i.para }) })),
    { id: "novo", grupo: "Ações", rotulo: "Novo lançamento", icone: <ListPlus size={16} aria-hidden />, aoEscolher: () => navegar({ to: "/lancamentos" }) },
  ];

  return (
    <>
      <PainelContexto.Provider value={setPainel}>
        <MolduraBase itens={itens} pessoa={eu} aoBuscar={() => abrirBusca(true)} aoSair={sair} painel={painel}>
          <Outlet />
        </MolduraBase>
      </PainelContexto.Provider>
      {buscaUsada && (
        <Suspense fallback={null}>
          <BuscaRapida aberta={busca} aoMudar={abrirBusca} itens={atalhos}
            aoBuscarProcesso={(termo) => navegar({ to: "/processos", search: { busca: termo } })} />
        </Suspense>
      )}
    </>
  );
}
